export class CatalogRepository {
    driver;
    constructor(driver) {
        this.driver = driver;
    }
    // ----- replica maintenance (pull-side) --------------------------------
    async upsertCategories(rows) {
        if (rows.length === 0)
            return;
        // Portable update-then-insert pairs (minSdk 26 SQLite has no upsert).
        await this.driver.batch(rows.flatMap((c) => [
            {
                sql: 'UPDATE categories SET name = ?, sort_order = ? WHERE id = ?',
                params: [c.name, c.sortOrder, c.id],
            },
            {
                sql: `INSERT INTO categories (id, name, sort_order)
                SELECT ?, ?, ? WHERE (SELECT changes()) = 0`,
                params: [c.id, c.name, c.sortOrder],
            },
        ]));
    }
    async upsertMenuItems(rows) {
        if (rows.length === 0)
            return;
        // Portable update-then-insert pairs (minSdk 26 SQLite has no upsert).
        await this.driver.batch(rows.flatMap((m) => {
            const values = [
                m.categoryId,
                m.sku,
                m.name,
                m.pricePaise,
                m.gstRateBp,
                m.hsn,
                m.station,
                m.isVegetarian ? 1 : 0,
                m.discountPctBp,
                m.foodCostPctBp,
                m.isActive ? 1 : 0,
                m.updatedAt,
            ];
            return [
                {
                    sql: `UPDATE menu_items SET category_id = ?, sku = ?, name = ?, price_paise = ?,
                    gst_rate_bp = ?, hsn = ?, station = ?, is_vegetarian = ?, discount_pct_bp = ?,
                    food_cost_pct_bp = ?, is_active = ?, updated_at = ?
                  WHERE id = ?`,
                    params: [...values, m.id],
                },
                {
                    sql: `INSERT INTO menu_items
                    (id, category_id, sku, name, price_paise, gst_rate_bp, hsn, station,
                     is_vegetarian, discount_pct_bp, food_cost_pct_bp, is_active, updated_at)
                  SELECT ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ? WHERE (SELECT changes()) = 0`,
                    params: [m.id, ...values],
                },
            ];
        }));
    }
    /** A deactivation learned from a tombstone/pull flips the flag - mid-shift
     *  carts that already snapshotted the item are unaffected by design. */
    async deactivateMenuItem(id) {
        await this.driver.execute({
            sql: 'UPDATE menu_items SET is_active = 0 WHERE id = ?',
            params: [id],
        });
    }
    // ----- POS reads ------------------------------------------------------
    async listCategories() {
        const rows = await this.driver.query({
            sql: 'SELECT * FROM categories ORDER BY sort_order, name',
        });
        return rows.map((r) => ({
            id: String(r.id),
            name: String(r.name),
            sortOrder: Number(r.sort_order),
        }));
    }
    async getMenuItem(id) {
        const rows = await this.driver.query({
            sql: 'SELECT * FROM menu_items WHERE id = ?',
            params: [id],
        });
        return rows[0] ? mapItem(rows[0]) : null;
    }
    async getMenuItemBySku(sku) {
        const rows = await this.driver.query({
            sql: 'SELECT * FROM menu_items WHERE sku = ?',
            params: [sku],
        });
        return rows[0] ? mapItem(rows[0]) : null;
    }
    /**
     * The POS search: name or SKU contains-match over active items, mirroring
     * today's client-side filter in Pos.tsx. Catalogue sizes (≤ a few thousand)
     * keep this sub-millisecond without FTS - measured in tests/performance.
     */
    async searchMenuItems(term, options = {}) {
        const { categoryId, activeOnly = true, limit = 200 } = options;
        const clauses = [];
        const params = [];
        if (activeOnly)
            clauses.push('is_active = 1');
        if (categoryId) {
            clauses.push('category_id = ?');
            params.push(categoryId);
        }
        const trimmed = term.trim();
        if (trimmed) {
            // SQLite LIKE is already case-insensitive for ASCII; ESCAPE guards % and _.
            clauses.push("(name LIKE ? ESCAPE '\\' OR sku LIKE ? ESCAPE '\\')");
            const like = `%${escapeLike(trimmed)}%`;
            params.push(like, like);
        }
        const where = clauses.length ? `WHERE ${clauses.join(' AND ')}` : '';
        params.push(limit);
        const rows = await this.driver.query({
            sql: `SELECT * FROM menu_items ${where} ORDER BY name LIMIT ?`,
            params,
        });
        return rows.map(mapItem);
    }
    async countMenuItems() {
        const rows = await this.driver.query({ sql: 'SELECT COUNT(*) AS n FROM menu_items' });
        return Number(rows[0]?.n ?? 0);
    }
}
function escapeLike(term) {
    return term.replace(/[%_]/g, (m) => `\\${m}`);
}
function mapItem(r) {
    return {
        id: String(r.id),
        categoryId: String(r.category_id),
        sku: String(r.sku),
        name: String(r.name),
        pricePaise: Number(r.price_paise),
        gstRateBp: Number(r.gst_rate_bp),
        hsn: String(r.hsn),
        station: String(r.station),
        isVegetarian: Number(r.is_vegetarian) === 1,
        discountPctBp: Number(r.discount_pct_bp),
        foodCostPctBp: Number(r.food_cost_pct_bp ?? 0),
        isActive: Number(r.is_active) === 1,
        updatedAt: r.updated_at == null ? null : String(r.updated_at),
    };
}
//# sourceMappingURL=catalog.js.map